import React from 'react';
import { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { HeadingLarge, BodySmall, HeadingMedium, Caption } from '@/components/ui/typography';

export type GradientTheme = 'hiring' | 'archive' | 'talent' | 'company' | 'pipeline' | 'internal';

interface HeroStat {
  value: string | number;
  label: string;
}

interface GradientHeroProps {
  icon: LucideIcon;
  title: string;
  description: string;
  theme: GradientTheme;
  stats?: HeroStat[];
  actionButton?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
  heroPrefix?: string; // For unique CSS class prefixes
  enableTranslations?: boolean; // Enable translation support
  titleKey?: string; // Translation key for title
  descriptionKey?: string; // Translation key for description
}

const getThemeColors = (theme: GradientTheme) => {
  const themes = {
    hiring: {
      base: 'from-orange-500/20 via-amber-400/15 to-sky-400/20 dark:from-orange-500/10 dark:via-amber-400/8 dark:to-sky-400/10',
      overlay: 'from-orange-500/[0.15] via-amber-400/[0.12] to-sky-400/[0.15] dark:from-orange-500/[0.08] dark:via-amber-400/[0.06] dark:to-sky-400/[0.08]',
      orb1: 'bg-orange-400/30 dark:bg-orange-400/20',
      orb2: 'bg-sky-400/25 dark:bg-sky-400/15',
    },
    archive: {
      base: 'from-purple-500/20 via-violet-400/15 to-indigo-400/20 dark:from-purple-500/10 dark:via-violet-400/8 dark:to-indigo-400/10',
      overlay: 'from-purple-500/[0.15] via-violet-400/[0.12] to-indigo-400/[0.15] dark:from-purple-500/[0.08] dark:via-violet-400/[0.06] dark:to-indigo-400/[0.08]',
      orb1: 'bg-purple-400/30 dark:bg-purple-400/20',
      orb2: 'bg-indigo-400/25 dark:bg-indigo-400/15',
    },
    talent: {
      base: 'from-emerald-500/20 via-green-400/15 to-teal-400/20 dark:from-emerald-500/10 dark:via-green-400/8 dark:to-teal-400/10',
      overlay: 'from-emerald-500/[0.15] via-green-400/[0.12] to-teal-400/[0.15] dark:from-emerald-500/[0.08] dark:via-green-400/[0.06] dark:to-teal-400/[0.08]',
      orb1: 'bg-emerald-400/30 dark:bg-emerald-400/20',
      orb2: 'bg-teal-400/25 dark:bg-teal-400/15',
    },
    company: {
      base: 'from-blue-500/20 via-sky-400/15 to-cyan-400/20 dark:from-blue-500/10 dark:via-sky-400/8 dark:to-cyan-400/10',
      overlay: 'from-blue-500/[0.15] via-sky-400/[0.12] to-cyan-400/[0.15] dark:from-blue-500/[0.08] dark:via-sky-400/[0.06] dark:to-cyan-400/[0.08]',
      orb1: 'bg-blue-400/30 dark:bg-blue-400/20',
      orb2: 'bg-cyan-400/25 dark:bg-cyan-400/15',
    },
    pipeline: {
      base: 'from-violet-500/20 via-purple-400/15 to-pink-400/20 dark:from-violet-500/10 dark:via-purple-400/8 dark:to-pink-400/10',
      overlay: 'from-violet-500/[0.15] via-purple-400/[0.12] to-pink-400/[0.15] dark:from-violet-500/[0.08] dark:via-purple-400/[0.06] dark:to-pink-400/[0.08]',
      orb1: 'bg-violet-400/30 dark:bg-violet-400/20',
      orb2: 'bg-pink-400/25 dark:bg-pink-400/15',
    },
    internal: {
      base: 'from-slate-500/20 via-gray-400/15 to-indigo-400/20 dark:from-slate-500/10 dark:via-gray-400/8 dark:to-indigo-400/10',
      overlay: 'from-slate-500/[0.15] via-gray-400/[0.12] to-indigo-400/[0.15] dark:from-slate-500/[0.08] dark:via-gray-400/[0.06] dark:to-indigo-400/[0.08]',
      orb1: 'bg-slate-400/30 dark:bg-slate-400/20',
      orb2: 'bg-indigo-400/25 dark:bg-indigo-400/15',
    },
  };
  return themes[theme];
};

export function GradientHero({
  icon: Icon,
  title,
  description,
  theme,
  stats = [],
  actionButton,
  className,
  isLoading = false,
  children,
  heroPrefix = 'gradient',
  enableTranslations = false,
  titleKey,
  descriptionKey
}: GradientHeroProps) {
  const { t } = useTranslation('pages');
  
  // Use translations if enabled and keys are provided
  const displayTitle = enableTranslations && titleKey ? t(titleKey) : title;
  const displayDescription = enableTranslations && descriptionKey ? t(descriptionKey) : description;
  const colors = getThemeColors(theme);

  return (
    <div className={cn(
      "relative overflow-hidden rounded-lg border",
      "bg-gradient-to-br",
      colors.base,
      className
    )}>
      {/* Base Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background/40 via-background/60 to-background/80 dark:from-background/60 dark:via-background/80 dark:to-background/95" />
      
      {/* Animated Overlay */}
      <div className={cn(
        "absolute inset-0 bg-gradient-to-r animate-pulse-soft",
        colors.overlay
      )} />
      
      {/* Decorative Blur Orbs */}
      <div className={cn(
        "absolute top-4 right-8 w-20 h-20 rounded-full blur-xl",
        colors.orb1
      )} />
      <div className={cn(
        "absolute bottom-4 left-8 w-16 h-16 rounded-full blur-lg",
        colors.orb2
      )} />
      
      {/* Content */}
      <div className="relative p-4 md:p-6">
        {/* Responsive layout: Stack on mobile, single row on tablet+ */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Left: Icon, Title, Description */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`${heroPrefix}-hero-icon p-2 rounded-md bg-muted/80 backdrop-blur-sm shrink-0`}>
              <Icon className="w-4 h-4 text-muted-foreground" />
            </div>
            
            <div className={`${heroPrefix}-hero-content flex-1 min-w-0`}>
              <HeadingLarge className={`${heroPrefix}-hero-title mb-1 break-words`}>{displayTitle}</HeadingLarge>
              <BodySmall className={`${heroPrefix}-hero-description text-muted-foreground break-words`}>
                {displayDescription}
              </BodySmall>
            </div>
          </div>
          
          {/* Right: Stats and action button */}
          {(stats.length > 0 || actionButton) && (
            <div className="flex items-center gap-3 flex-shrink-0">
              {stats.length > 0 && (
                <div className={`${heroPrefix}-hero-stats flex gap-3`}>
                  {stats.map((stat, index) => (
                    <div key={index} className={`${heroPrefix}-hero-stat bg-background/60 backdrop-blur-sm rounded-md p-2 text-center shadow-sm min-w-[70px]`}>
                      <HeadingMedium className={cn(
                        `${heroPrefix}-hero-stat-value`,
                        isLoading && "animate-pulse"
                      )}>{stat.value}</HeadingMedium>
                      <Caption className={`${heroPrefix}-hero-stat-label uppercase tracking-wide`}>{stat.label}</Caption>
                    </div>
                  ))}
                </div>
              )}
              
              {actionButton && (
                <Button 
                  onClick={actionButton.onClick}
                  size="sm"
                  variant="ghost"
                  disabled={isLoading}
                  className={`${heroPrefix}-hero-button bg-background/60 backdrop-blur-sm shadow-sm hover:bg-background/80 hover:shadow-md h-9 text-sm whitespace-nowrap px-3`}
                >
                  {actionButton.label}
                </Button>
              )}
            </div>
          )}
        </div>
        
        {children}
      </div>
    </div>
  );
}